<script setup lang="ts">
  import type { Component } from 'vue';
  import { ref, computed, onMounted, onUnmounted, provide, watch } from 'vue';
  import { onBeforeRouteLeave, useRoute } from 'vue-router';
  import {
    FieldSearchBar,
    SearchToolbar,
    SelectInput,
    NoDocuments,
    TabsMenu,
    PaginateOrScroll,
  } from '@/shared/ui';
  import { DocumentFilterModal } from '@/features/Document';
  import { useApprovalOrVerificationSearch } from '@/features/Search';
  import { useFilterStore } from '@/entities/Filter';
  import { useTableFilterModal } from '@/shared/lib/use';
  import { useDocumentSearchTypeStore } from '@/shared/lib';
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';
  import type { SearchParams, SearchFilter } from '@/shared/lib';
  import {
    VERIFICATION_TAB_OPTIONS,
    VERIFICATION_DEFAULT_ITEMS_PER_PAGE,
    VERIFICATION_DEFAULT_SORT,
    getVerificationTabConfig,
    isVerificationDocType,
    type VerificationDocType,
  } from '../../config';

  /**
   * Objeto de padroes que o merge de filtros, o reset de troca de aba e o "Limpar filtros"
   * espalham: chave que falta aqui nao e zerada por nenhum desses caminhos, fica no filtro,
   * continua na URL e continua indo para a API.
   */
  const buildDefaultVerificationFilter = (documentType: VerificationDocType = 'DOCUMENT') => ({
    revisionStatus: '',
    type: documentType,
    revision: '',
    user: '',
    elaboratedBy: '',
    approvedBy: '',
    requestDate: '',
  });

  const props = withDefaults(
    defineProps<{
      title?: string;
      tableComponent: Component;
      emptyStateTitle?: string;
    }>(),
    {
      title: 'Etapa de Verificação e Vigência',
      emptyStateTitle: undefined,
    }
  );

  const route = useRoute();
  const { setSearchType } = useDocumentSearchTypeStore();

  // The shared store starts at `name`, but this screen defaults to "Registro" (`prefix`). A URL
  // that carries `searchType` wins, or `?searchType=name&term=X` would lose its field on reload.
  if (!route.query.searchType) {
    setSearchType('prefix');
  }

  const initialDocumentType = isVerificationDocType(route.query.type)
    ? route.query.type
    : 'DOCUMENT';

  const selectedDocumentType = ref<VerificationDocType>(initialDocumentType);

  const tabConfig = computed(() => getVerificationTabConfig(selectedDocumentType.value));

  const resolvedEmptyTitle = computed(() => props.emptyStateTitle ?? tabConfig.value.emptyTitle);

  const initialSearchParams: SearchParams = {
    search: '',
    sort: VERIFICATION_DEFAULT_SORT,
    filter: buildDefaultVerificationFilter(initialDocumentType),
    page: 1,
    amount: VERIFICATION_DEFAULT_ITEMS_PER_PAGE,
    persistQuantityInUrl: true,
    // Getter, e nao valor: cada aba le a sua fonte, e a troca de aba precisa trocar a URL da busca.
    get searchUrl() {
      return getVerificationTabConfig(selectedDocumentType.value).searchUrl;
    },
    systemFilters: ['type'],
  };

  const {
    items: revisions,
    metadata,
    selectedQuantity,
    allItems: allRevisions,
    hasMoreItems: hasMoreRevisions,
    loadingMore,
    shouldShowTable,
    shouldShowEmptyPage,
    shouldShowNoResults,
    isUsingFilters,
    handleSearch,
    handleFilter: baseHandleFilter,
    handleSortOptions,
    onPageChanged,
    handleQuantityChange,
    applyListQueryReset,
    initSearch,
    cleanup,
    filter,
    searchTerm,
    sort,
    order,
  } = useApprovalOrVerificationSearch(initialSearchParams);

  provide(SortOrderInjectionKey, { sort, order });

  const showListChrome = computed(
    () => shouldShowTable.value && !shouldShowNoResults.value && !shouldShowEmptyPage.value
  );

  const filterStore = useFilterStore();

  /**
   * `user` e zerada depois do espalhamento, e nao pelo objeto de padroes.
   *
   * O `FIELD_MAPPING` e bidirecional: ele carrega um `?user=` da URL para o campo
   * `elaborationApproval` do modal e o reescreve na saida, mesmo com o campo em branco na tela,
   * porque esta variante liga o select em `elaboratedBy`. Sem esta linha, um link antigo com
   * `?user=N` continua cortando a lista depois de aplicar filtros, com o modal exibindo
   * "Elaboração" vazia. A tela endereça o elaborador so por `elaboratedBy`, que e a chave que as
   * duas filas declaram.
   */
  const handleFilterMerged = async (filters: SearchFilter) => {
    const rest = { ...filters };
    delete rest.type;
    await baseHandleFilter({
      ...buildDefaultVerificationFilter(selectedDocumentType.value),
      ...rest,
      user: '',
      type: selectedDocumentType.value,
    });
  };

  const { openFilterModal } = useTableFilterModal(
    DocumentFilterModal,
    filterStore,
    handleFilterMerged,
    () => {
      const other = { ...filter.value };
      delete other.type;
      return other as SearchFilter;
    },
    {
      variant: 'verification',
      elaborationPermission: { action: 'send', subject: 'Document' },
      onClearFilters: () => {
        filterStore.clearFilters();
        void handleFilterMerged({
          ...buildDefaultVerificationFilter(selectedDocumentType.value),
          type: selectedDocumentType.value,
        });
      },
    }
  );

  const handleFilterClicked = () => {
    openFilterModal();
  };

  const resetListStateForTabChange = async (nextType: VerificationDocType) => {
    selectedDocumentType.value = nextType;
    filterStore.clearFilters();
    setSearchType('prefix');
    await applyListQueryReset({
      search: '',
      sort: VERIFICATION_DEFAULT_SORT,
      filter: buildDefaultVerificationFilter(nextType),
      quantity: VERIFICATION_DEFAULT_ITEMS_PER_PAGE,
    });
  };

  const handleTabSelected = async (optionId: string) => {
    const nextType = optionId as VerificationDocType;
    if (nextType === selectedDocumentType.value) {
      return;
    }
    await resetListStateForTabChange(nextType);
  };

  const hasMounted = ref(false);

  // A aba tambem muda pela URL, e nao so pelo clique: voltar no navegador troca `?type=` sem passar
  // por `resetListStateForTabChange`. Como cada aba le uma tabela diferente, so trocar o rotulo
  // deixaria as linhas da aba anterior na tela, sob os titulos da nova. A recarga so acontece
  // depois da montagem, porque `onMounted` ja faz a primeira busca.
  watch(
    () => filter.value.type,
    (filterType) => {
      if (isVerificationDocType(filterType) && selectedDocumentType.value !== filterType) {
        selectedDocumentType.value = filterType;
        if (hasMounted.value) {
          void initSearch();
        }
      }
    },
    { immediate: true }
  );

  // Uma ordenacao que a aba atual nao oferece volta ao padrao: sem isso, trocar de aba com a
  // ordenacao por numero de revisoes ativa manda `sort=revision` para a fila de matriz, que recusa.
  watch(
    [sort, tabConfig],
    ([currentSort, currentTabConfig]) => {
      if (currentSort === '') {
        return;
      }
      const isOfferedByTab = currentTabConfig.orderOptions.some(
        (option) => option.sort === currentSort
      );
      if (!isOfferedByTab) {
        void handleSortOptions('', VERIFICATION_DEFAULT_SORT.order);
      }
    },
    { immediate: true }
  );

  onMounted(async () => {
    await initSearch();
    hasMounted.value = true;
  });

  onUnmounted(() => {
    cleanup();
  });

  onBeforeRouteLeave(() => {
    filterStore.clearFilters();
  });
</script>

<template>
  <div class="pt-4 p-6">
    <h3 class="text-gray-07 text-xl/7.5 font-semibold text-left mb-4">
      {{ props.title }}
    </h3>

    <TabsMenu
      :options="VERIFICATION_TAB_OPTIONS"
      :selected-option="selectedDocumentType"
      class="mb-4"
      @option-selected="handleTabSelected"
    />

    <div
      v-if="!shouldShowEmptyPage"
      data-testid="verification-controls-row"
      class="mb-6"
    >
      <SearchToolbar>
        <template #search>
          <FieldSearchBar
            :search-fields="tabConfig.searchFields"
            :initial-search-term="searchTerm"
            class="w-full"
            @search="handleSearch"
          />
        </template>
        <template
          v-if="showListChrome"
          #quantity
        >
          <SelectInput
            v-model="selectedQuantity"
            data-testid="verification-quantity-select"
            :options="tabConfig.quantityOptions"
            class="cursor-pointer"
            @update:model-value="handleQuantityChange"
          />
        </template>
      </SearchToolbar>
    </div>

    <PaginateOrScroll
      v-if="showListChrome"
      :items="revisions"
      :metadata="metadata"
      :selected-quantity="selectedQuantity"
      :all-items="allRevisions"
      :loading-more="loadingMore"
      :has-more-items="hasMoreRevisions"
      :entity-name="tabConfig.paginationEntity"
      @page-changed="onPageChanged"
    >
      <template #table="{ items }">
        <component
          :is="props.tableComponent"
          :revisions="items"
          :page="metadata?.page ?? 1"
          :is-filter-active="isUsingFilters"
          :order-options="tabConfig.orderOptions"
          :code-column-label="tabConfig.codeColumnLabel"
          @update:sort="handleSortOptions"
          @update:filter="handleFilterMerged"
          @filter-clicked="handleFilterClicked"
        />
      </template>
    </PaginateOrScroll>
    <NoDocuments
      v-if="shouldShowEmptyPage"
      :title="resolvedEmptyTitle"
    />
    <NoDocuments
      v-else-if="shouldShowNoResults"
      title="Nenhum resultado encontrado"
    />
  </div>
</template>
